/**
 * Hook to manage wallet execution markers on the chart
 * Fetches profiles for traders and creates chart markers for their executions
 */

import { useEffect, useState, useMemo, useRef } from "react";
import { usePumpPortalTrades } from "./usePumpPortalTrades";
import { batchFetchPumpFunProfiles, type PumpFunProfile } from "@/lib/pumpfunProfile";
import type { UTCTimestamp } from "lightweight-charts";

export interface WalletExecutionMarker {
  time: UTCTimestamp;
  position: "aboveBar" | "belowBar";
  shape: "circle" | "square" | "arrowUp" | "arrowDown";
  color: string;
  size: number;
  id: string;
  trader: string;
  profile?: PumpFunProfile;
  solAmount: number;
  isBuy: boolean;
}

export function useWalletExecutionMarkers(
  mint: string | null,
  maxMarkers: number = 50,
  enabled: boolean = true
) {
  const { rows: trades } = usePumpPortalTrades(mint, 200);
  const [markers, setMarkers] = useState<WalletExecutionMarker[]>([]);
  const [profiles, setProfiles] = useState<Map<string, PumpFunProfile>>(new Map());
  const fetchingProfilesRef = useRef<Set<string>>(new Set());

  // Extract unique trader addresses from trades
  const uniqueTraders = useMemo(() => {
    const traders = new Set<string>();
    for (const trade of trades) {
      if (trade.trader) {
        traders.add(trade.trader);
      }
    }
    return Array.from(traders);
  }, [trades]);

  // Fetch profiles for unique traders
  useEffect(() => {
    if (!enabled || uniqueTraders.length === 0) return;

    // Only fetch profiles for traders we haven't fetched yet
    const tradersToFetch = uniqueTraders.filter(
      (trader) => !profiles.has(trader) && !fetchingProfilesRef.current.has(trader)
    );

    if (tradersToFetch.length === 0) return;

    // Mark as fetching to avoid duplicate requests
    tradersToFetch.forEach((trader) => fetchingProfilesRef.current.add(trader));

    // Batch fetch profiles (limit to 10 at a time since they're now fast)
    const batchSize = 10;
    for (let i = 0; i < tradersToFetch.length; i += batchSize) {
      const batch = tradersToFetch.slice(i, i + batchSize);
      
      batchFetchPumpFunProfiles(batch).then((newProfiles) => {
        setProfiles((prev) => {
          const updated = new Map(prev);
          newProfiles.forEach((profile, address) => {
            updated.set(address, profile);
          });
          return updated;
        });
        
        // Remove from fetching set
        batch.forEach((trader) => fetchingProfilesRef.current.delete(trader));
      });
    }
  }, [uniqueTraders, profiles, enabled]);

  // Create markers from trades
  useEffect(() => {
    if (!enabled || trades.length === 0) {
      setMarkers([]);
      return;
    }

    const newMarkers: WalletExecutionMarker[] = [];
    
    // Group sequential trades by the same trader to match the Signal Evidence panel
    const groups: any[] = [];
    let currentGroup: any = null;
    
    // Trades are typically newest-first. Process them as they are to group contiguous ones.
    for (const trade of trades) {
      if (!trade.trader) continue;
      
      if (!currentGroup || currentGroup.trader !== trade.trader) {
        if (currentGroup) groups.push(currentGroup);
        currentGroup = {
          trader: trade.trader,
          buy: trade.buy,
          sol: trade.sol,
          ts: trade.ts,
        };
      } else {
        currentGroup.sol += trade.sol;
        currentGroup.ts = Math.max(currentGroup.ts, trade.ts);
      }
    }
    if (currentGroup) groups.push(currentGroup);

    const significantGroups = groups.filter(g => g.sol > 0);
    console.log('[Wallet Markers] Total significant groups:', significantGroups.length);

    // Convert to markers
    for (const trade of significantGroups) {
      const timeSec = Math.floor(trade.ts / 1000);
      const profile = profiles.get(trade.trader);
      
      // Color based on buy/sell and whether it's a whale
      const isWhale = profile?.isWhale || false;
      const buyColor = isWhale ? "#FFD700" : "#14F195"; // Gold for whales, green for regular
      const sellColor = isWhale ? "#FF6B6B" : "#FF4557"; // Lighter red for whales, regular red
      
      const marker: WalletExecutionMarker = {
        time: timeSec as UTCTimestamp,
        position: trade.buy ? "belowBar" : "aboveBar",
        shape: "circle",
        color: trade.buy ? buyColor : sellColor,
        size: isWhale ? 1.2 : 1.0, // Larger markers for better visibility
        id: `exec-${trade.trader}-${timeSec}`,
        trader: trade.trader,
        profile,
        solAmount: trade.sol,
        isBuy: trade.buy,
      };
      
      newMarkers.push(marker);
    }

    console.log('[Wallet Markers] Created markers:', newMarkers.length);

    // Sort by time and limit to maxMarkers
    newMarkers.sort((a, b) => (a.time as number) - (b.time as number));
    setMarkers(newMarkers.slice(0, maxMarkers));
  }, [trades, profiles, enabled, maxMarkers]);

  return { markers, profiles, isLoading: fetchingProfilesRef.current.size > 0 };
}
